/* eslint-disable @typescript-eslint/no-explicit-any */
import type { BuilderComponentState } from '@builder.io/react/dist/types/src/components/builder-component.component';

export const isBuilderDashboard = (searchParams: Record<string, string>) =>
  searchParams?.['__builder_editing__'] === 'true';

const componentIsBuilder = (component: React.ReactElement) => !!component?.props?.block?.component?.name;

const getElement = (component: React.ReactElement) => {
  const isBuilder = componentIsBuilder(component);

  const element = isBuilder ? component : { ...component };
  const props = isBuilder ? element.props.block.component : element;
  const options = isBuilder ? 'options' : 'props';
  // @ts-expect-error display name exists on type
  const name = isBuilder ? element.props.block.component.name : element?.type?.displayName;

  return { isBuilder, element, props, options, name };
};

export const addPropsToAllChildren = (
  children: React.ReactElement | React.ReactElement[],
  newProps: Record<string, string | boolean | undefined>,
) => {
  if (!children) {
    return null;
  }
  const childArray = Array.isArray(children) ? children : [children];

  return (
    childArray.length > 0 &&
    childArray.map((child, index) => {
      const { element, props, options } = getElement(child);
      props[options] = { ...props[options], ...newProps, index };

      return element;
    })
  );
};

export const isReference = <T>(data: BuilderReference<T>) => data?.value?.data;
export const getReference = <T>(data: BuilderReference<T>) => data?.value?.data;

const isMapLike = (v: unknown) => !!v && typeof v === 'object' && typeof (v as any).entries === 'function';

const toPlain = <T = unknown>(v: any): T =>
  isMapLike(v) ? (Object.fromEntries((v as Map<string, unknown>).entries()) as T) : (v as T);

export const getBuilderStateData = (key: string, builderState: BuilderComponentState) => {
  const data = builderState?.state?.[key];
  if (data == null) {
    return null;
  }

  let parsedData: any = null;

  try {
    if (typeof data === 'string') {
      const s = data.trim();
      if (s.startsWith('{') || s.startsWith('[')) {
        parsedData = JSON.parse(s);
      } else {
        return null;
      }
    } else if (typeof data === 'object') {
      parsedData = toPlain(data);
    } else {
      return null;
    }
  } catch {
    return null;
  }

  if (parsedData && typeof parsedData === 'object' && 'value' in parsedData) {
    parsedData.value = toPlain(parsedData.value);
    if (parsedData.value && typeof parsedData.value === 'object' && 'data' in parsedData.value) {
      parsedData.value.data = toPlain(parsedData.value.data);
    }
  }

  const rawData = isReference(parsedData) ? getReference(parsedData) : parsedData;

  return rawData ?? null;
};
